/**
 * Le numéro qu'un visiteur a choisi AVANT d'avoir un compte.
 *
 * Les pages publiques `/numero-local/<indicatif>` affichent de vrais numéros
 * libres. Quand le visiteur en clique un, il part vers l'inscription avec
 * `?area=604&number=%2B16045551234`. Or le choix du numéro n'a lieu qu'à
 * l'étape 1 de l'onboarding, soit après la création du compte et, souvent,
 * après la vérification de l'adresse courriel : deux ou trois rechargements
 * plus loin. Sans mémoire, la sélection meurt sur la première page.
 *
 * D'où ce dépôt. `capturer()` ramasse l'intention à l'arrivée sur
 * l'inscription, `lire()` la rend à l'étape 1, `oublier()` l'efface dès
 * qu'elle a servi, pour qu'une inscription suivante dans le même navigateur
 * ne réveille pas le choix de la précédente.
 *
 * On stocke une intention, jamais une réservation : le numéro a pu être
 * pris entre-temps. L'étape 1 le repropose s'il est encore libre, et se
 * rabat sans rien dire sur l'indicatif s'il ne l'est plus.
 */

const CLE = 'callizy_numero_souhaite';

/** Durée de vie de l'intention. Au-delà, le numéro n'a plus de sens. */
const EXPIRE_MS = 24 * 60 * 60 * 1000;

export interface NumeroSouhaite {
  /** Indicatif à trois chiffres, seul élément dont l'étape 1 a besoin. */
  area: string;
  /** Numéro précis en E.164, si le visiteur en a cliqué un plutôt que de chercher. */
  number?: string;
  /** Horodatage de la capture, pour la péremption. */
  at: number;
}

/** Les accès au stockage échouent en navigation privée : jamais bloquant. */
function sansEchec<T>(f: () => T): T | null {
  try {
    return f();
  } catch {
    return null;
  }
}

/**
 * Ramasse `?area=` et `?number=` dans l'URL courante et les met de côté.
 *
 * Ne fait rien si l'URL ne porte pas d'indicatif valide, ce qui couvre la
 * quasi-totalité des inscriptions, qui n'arrivent pas des pages publiques.
 */
export function capturer(search: string): void {
  const p = new URLSearchParams(search);
  const area = (p.get('area') || '').trim();
  if (!/^\d{3}$/.test(area)) return;

  const brut = (p.get('number') || '').trim();
  // On n'accepte qu'un E.164 nord-américain, et seulement s'il appartient
  // bien à l'indicatif annoncé : un paramètre d'URL est une saisie comme
  // une autre, et il finit ici dans un champ de commande.
  const number = /^\+1\d{10}$/.test(brut) && brut.slice(2, 5) === area ? brut : undefined;

  sansEchec(() =>
    localStorage.setItem(CLE, JSON.stringify({ area, number, at: Date.now() } satisfies NumeroSouhaite)),
  );
}

/** Rend l'intention si elle existe et n'a pas plus de 24 h. */
export function lire(): NumeroSouhaite | null {
  const brut = sansEchec(() => localStorage.getItem(CLE));
  if (!brut) return null;

  const v = sansEchec(() => JSON.parse(brut) as NumeroSouhaite);
  if (!v || !/^\d{3}$/.test(v.area || '')) return null;
  if (!v.at || Date.now() - v.at > EXPIRE_MS) {
    oublier();
    return null;
  }
  return v;
}

/** Efface l'intention. Appelé dès que l'étape 1 s'en est servie. */
export function oublier(): void {
  sansEchec(() => localStorage.removeItem(CLE));
}
